"use client";

import React, { useState } from "react";

type TextRole = "heading" | "body" | "list";

function ToolIcon({ type }: { type: "text" | "image" | "clipart" | "shapes" | "templates" | "background" | "pages" }) {
  if (type === "text") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 6h12M12 6v12M9 18h6" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === "image") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="4" y="4" width="16" height="16" rx="2" stroke="currentColor" strokeWidth="2" />
        <circle cx="9" cy="9" r="1.6" fill="currentColor" />
        <path d="m20 15-4.5-4.5L7 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "clipart") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 19.5h12a2 2 0 0 0 2-2v-11a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2Z" stroke="currentColor" strokeWidth="1.9" />
        <path d="M8 15.5 10.8 12l2.2 2.4 2.8-4.1L20 15.5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        <circle cx="8.4" cy="8.4" r="1.4" fill="currentColor" />
      </svg>
    );
  }

  if (type === "shapes") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M14.5 4.5h5v5h-5z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
        <circle cx="7.5" cy="16.5" r="3.5" stroke="currentColor" strokeWidth="1.9" />
        <path d="M8 4.5 12 11H4L8 4.5Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        <path d="m15 14 5 5M20 14l-5 5" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === "templates") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <rect x="3.5" y="4" width="17" height="16" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8.75 4v16M15.25 4v16M3.5 9.5h17M3.5 14.5h11.75" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M15.25 14.5h5.25" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === "background") {
    return (
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="m5 12.5 6.7-6.7a2 2 0 0 1 2.8 0l3.7 3.7a2 2 0 0 1 0 2.8L12 18.5a2.2 2.2 0 0 1-3.1 0L5 14.6a1.5 1.5 0 0 1 0-2.1Z" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8.2 9.3h8.1M4.8 13.4h14.4" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" />
        <path d="M19 16.2s2 2.1 2 3.4a2 2 0 0 1-4 0c0-1.3 2-3.4 2-3.4Z" stroke="currentColor" strokeWidth="1.9" strokeLinejoin="round" />
      </svg>
    );
  }

  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 4h10a2 2 0 0 1 2 2v14H8a2 2 0 0 1-2-2V4Z" stroke="currentColor" strokeWidth="2" />
      <path d="M8 4v14a2 2 0 0 0 2 2" stroke="currentColor" strokeWidth="2" />
    </svg>
  );
}

function ToolbarButton({
  icon,
  label,
  onClick,
  disabled,
  onboardingTarget,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  onboardingTarget?: string;
}) {
  return (
    <button
      type="button"
      data-onboarding-target={onboardingTarget}
      className="flex min-w-[64px] flex-col items-center gap-1 rounded-lg px-3 py-2 text-xs font-semibold text-white/72 transition-colors hover:bg-white/5 hover:text-white disabled:opacity-40"
      onClick={onClick}
      disabled={disabled}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

export default function MobileEditorToolbar({
  locale,
  readOnly,
  onAddTextRole,
  onOpenImages,
  onOpenClipart,
  onOpenShapes,
  onOpenTemplates,
  onAddBackground,
  onShowPages,
}: {
  locale: string;
  readOnly?: boolean;
  onAddTextRole: (role: TextRole) => void;
  onOpenImages: () => void;
  onOpenClipart: () => void;
  onOpenShapes: () => void;
  onOpenTemplates: () => void;
  onAddBackground: () => void;
  onShowPages: () => void;
}) {
  const [textMenuOpen, setTextMenuOpen] = useState(false);
  const isPl = locale === "pl";

  function addText(role: TextRole) {
    if (readOnly) return;
    onAddTextRole(role);
    setTextMenuOpen(false);
  }

  return (
    <div className="fixed inset-x-0 bottom-0 z-[80] md:hidden">
      {textMenuOpen ? (
        <div className="border-t border-white/10 bg-[#1b2436] px-3 py-2 shadow-2xl">
          <div className="flex justify-center gap-2">
            <button
              type="button"
              className="rounded-lg bg-white/8 px-4 py-2 text-sm font-semibold text-white hover:bg-white/12"
              onClick={() => addText("body")}
              disabled={readOnly}
            >
              {isPl ? "Tekst" : "Text"}
            </button>
            <button
              type="button"
              className="rounded-lg bg-white/8 px-4 py-2 text-sm font-semibold text-white hover:bg-white/12"
              onClick={() => addText("heading")}
              disabled={readOnly}
            >
              {isPl ? "Nagłówek" : "Heading"}
            </button>
            <button
              type="button"
              className="rounded-lg bg-white/8 px-4 py-2 text-sm font-semibold text-white hover:bg-white/12"
              onClick={() => addText("list")}
              disabled={readOnly}
            >
              {isPl ? "Lista" : "List"}
            </button>
          </div>
        </div>
      ) : null}

      <div className="border-t border-white/10 bg-[#141b31] px-2 pb-[calc(0.75rem+env(safe-area-inset-bottom))] pt-2 shadow-2xl">
        <div className="flex items-center gap-1 overflow-x-auto">
          <ToolbarButton
            icon={<ToolIcon type="text" />}
            label={isPl ? "Tekst" : "Text"}
            onboardingTarget="mobile-tool-text"
            onClick={() => setTextMenuOpen((open) => !open)}
            disabled={readOnly}
          />
          <ToolbarButton
            icon={<ToolIcon type="image" />}
            label={isPl ? "Zdjęcia" : "Images"}
            onboardingTarget="mobile-tool-image"
            onClick={() => {
              setTextMenuOpen(false);
              onOpenImages();
            }}
            disabled={readOnly}
          />
          <ToolbarButton
            icon={<ToolIcon type="clipart" />}
            label="Clipart"
            onboardingTarget="mobile-tool-clipart"
            onClick={() => {
              setTextMenuOpen(false);
              onOpenClipart();
            }}
            disabled={readOnly}
          />
          <ToolbarButton
            icon={<ToolIcon type="shapes" />}
            label={isPl ? "Kształty" : "Shapes"}
            onboardingTarget="mobile-tool-shapes"
            onClick={() => {
              setTextMenuOpen(false);
              onOpenShapes();
            }}
            disabled={readOnly}
          />
          <ToolbarButton
            icon={<ToolIcon type="templates" />}
            label={isPl ? "Motywy" : "Themes"}
            onboardingTarget="mobile-tool-templates"
            onClick={() => {
              setTextMenuOpen(false);
              onOpenTemplates();
            }}
            disabled={readOnly}
          />
          <ToolbarButton
            icon={<ToolIcon type="background" />}
            label={isPl ? "Tło" : "Bg"}
            onboardingTarget="mobile-tool-background"
            onClick={() => {
              setTextMenuOpen(false);
              onAddBackground();
            }}
            disabled={readOnly}
          />
          <ToolbarButton
            icon={<ToolIcon type="pages" />}
            label={isPl ? "Strony" : "Pages"}
            onboardingTarget="mobile-tool-pages"
            onClick={() => {
              setTextMenuOpen(false);
              onShowPages();
            }}
          />
        </div>
      </div>
    </div>
  );
}
